import React, { forwardRef, useCallback, useContext, useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import NoteAddIcon from '@mui/icons-material/NoteAddRounded';
import BugReportIcon from '@mui/icons-material/BugReportRounded';
import AccountTreeIcon from '@mui/icons-material/AccountTreeRounded';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpwardRounded';
import ClearIcon from '@mui/icons-material/ClearRounded';
import SyncIcon from '@mui/icons-material/SyncRounded';
import SplitIcon from '@mui/icons-material/CallSplitRounded';
import withStyles from '@mui/styles/withStyles';
import Dialog from '@mui/material/Dialog';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import SvgIcon from '@mui/material/SvgIcon';
import { useMessage } from 'studio/components/Message';
import { useConfirmationModal } from 'studio/components/ConfirmationModal/ConfirmationModal';
import CreateFileForm from 'studio/pages/Editor/CreateFileForm';
import PopoverButton from 'components/Popover/PopoverButton';
import { useLoader } from 'components/loaders/FadeLoader';
import services from 'services';
import styles from 'studio/pages/Editor/Editor.styles';
import HierarchyIcon from 'svg/hierarchy.svg';
import FolderIcon from '@mui/icons-material/FolderRounded';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useCreateDevKg } from 'studio/components/CreateDevKg/CreateDevKg';
import { KgPermissions } from 'studio/constants';
import { EditorContext, treeTypes } from 'studio/pages/Editor/Editor';
import { creatableFileTypes, fileTypesWithFixedIdentifier } from 'studio/pages/Editor/FileTypeConstants';
import { useEditorUtils } from 'studio/pages/Editor/EditorUtils';
import TestResultModal from 'studio/pages/Editor/TestResultModal';
import IconButton from '@mui/material/IconButton';

const ActionButton = forwardRef(({ tooltip, icon, onClick, ...props }, ref) => (
  <Tooltip title={tooltip} placement="top" arrow>
    <div>
      <IconButton size="large" onClick={onClick} ref={ref} {...props}>
        {React.createElement(icon, { fontSize: 'small' })}
      </IconButton>
    </div>
  </Tooltip>
));

/**
 * Action bar in the left panel of the editor / studio containing actions like creating a new file and switching between the object and file tree
 */
const EditorLeftPanelActionBar = ({ classes, treeType, setTreeType }) => {
  const {
    kgId,
    kgPermissions,
    knowledgeGraph,
    fetchKnowledgeGraphs,
    user,
    navigation: { openInEditor },
    fileState: { setFiles },
    refs: { conceptTreeRef }
  } = useContext(EditorContext);

  const { operationAffectingMultipleFiles } = useEditorUtils();

  const { withLoader } = useLoader();
  const { successMessage, errorMessage } = useMessage();

  const { showConfirmationModal } = useConfirmationModal();

  const { createDevKnowledgeGraph, CreateDevKgModal } = useCreateDevKg(knowledgeGraph, fetchKnowledgeGraphs, user);

  const [newFileType, setNewFileType] = useState(null);
  const [newFileIdentifier, setNewFileIdentifier] = useState(null);
  const [fileIsCreating, setFileIsCreating] = useState(false);
  // whether the Git operations menu is open
  const [isGitOperationsMenuOpen, setIsGitOperationsMenuOpen] = useState(false);

  const [testResultState, setTestResultState] = useState({
    isOpen: false,
    confirmButtonText: undefined,
    onConfirm: undefined
  });
  const [testResult, setTestResult] = useState(null);

  const gitOperationRequest = useCallback(
    async (op, successMessageText, defaultErrorMessageText, title, confirmationText, confirmButtonText, callback) => {
      setIsGitOperationsMenuOpen(false);

      operationAffectingMultipleFiles(
        title,
        'This operation possibly',
        'performing the operation',
        null,
        async reloadFrontend => {
          showConfirmationModal(() => ({
            title,
            content: confirmationText,
            confirmButtonText: confirmButtonText || title,
            onConfirm: async () => {
              const response = await withLoader(op(kgId));
              if (response.success) {
                successMessage(successMessageText);
                reloadFrontend(null);
                callback?.();
              } else {
                const message = response.response?.data || defaultErrorMessageText;
                errorMessage(message);
              }
            }
          }));
        }
      );
    },
    [
      kgId,
      withLoader,
      successMessage,
      errorMessage,
      openInEditor,
      operationAffectingMultipleFiles,
      showConfirmationModal
    ]
  );

  const createFile = useCallback(async () => {
    const identifier = newFileIdentifier || fileTypesWithFixedIdentifier.get(newFileType);
    const response = await services.createKnowledgeGraphFile(kgId, newFileType, identifier);
    setFileIsCreating(false);
    if (response.success) {
      const file = response.data;
      successMessage(`New file "${file.data.name}" created successfully!`);
      setFiles(files => ({ ...files, [file.data.path]: file }));
      openInEditor(file.data.path);
      // update the concept tree
      conceptTreeRef.current.reloadTreeItems([file]);
    } else {
      const message = response.response?.data?.data?.message || 'There was an error creating the knowledge graph';
      errorMessage(message);
    }
  }, [kgId, newFileType, newFileIdentifier, openInEditor, withLoader, successMessage, errorMessage]);

  const checkDevKgIsUpToDateBeforeTesting = useCallback(
    async op => {
      if (knowledgeGraph?.data?.developmentKgForId) {
        const branchComparison = await withLoader(services.compareDevKgToMainKg(kgId));
        if (branchComparison.data.remoteIsAheadOfLocal) {
          gitOperationRequest(
            kgId => services.pullDevKgFromMainKg(kgId),
            'Knowledge Graph successfully pulled from main knowledge graph!',
            'There was an error pulling the knowledge graph from main knowledge graph.',
            'Pull from main knowledge graph',
            'There are changes in the main knowledge graph. In order to run the tests, you need to pull these changes into the development knowledge graph.',
            'Pull & Test',
            () => op()
          );
        } else {
          op();
        }
      } else {
        op();
      }
    },
    [knowledgeGraph, gitOperationRequest, withLoader, kgId]
  );

  // https://github.com/mui/material-ui/issues/18091
  const TooltipToggleButton = forwardRef(({ TooltipProps, ...props }, ref) => {
    const StyledTooltip = withStyles({ tooltip: { marginTop: -50 } })(Tooltip);
    return (
      <StyledTooltip {...TooltipProps} arrow>
        <ToggleButton ref={ref} {...props} />
      </StyledTooltip>
    );
  });

  const commonKgGitAndFilesActions = user?.isAdmin && (
    <>
      <ListItem disablePadding>
        <ListItemButton
          dense
          onClick={_ =>
            gitOperationRequest(
              kgId => services.reloadKnowledgeGraphFromFiles(kgId),
              'Knowledge Graph successfully reloaded from files!',
              'There was an error reloading the knowledge graph from files.',
              'Reload from files',
              'Do you want to reload the knowledge graph from the local files?',
              'Reload'
            )
          }
        >
          <ListItemIcon>
            <SyncIcon />
          </ListItemIcon>
          <ListItemText primary="Reload knowledge graph from files" />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding>
        <ListItemButton
          dense
          onClick={_ =>
            gitOperationRequest(
              kgId => services.pullKnowledgeGraphFromRemote(kgId),
              'Knowledge Graph successfully pulled from remote!',
              'There was an error pulling the knowledge graph from remote.',
              'Pull from remote',
              'Do you want to pull changes from remote?',
              'Pull'
            )
          }
          data-test="pullKnowledgeGraphFromRemote"
        >
          <ListItemIcon>
            <ArrowDownwardIcon />
          </ListItemIcon>
          <ListItemText primary="Pull changes from remote" />
        </ListItemButton>
      </ListItem>
    </>
  );
  const gitOperationsPopoverButton = (
    <PopoverButton
      isOpen={isGitOperationsMenuOpen}
      onOpened={() => setIsGitOperationsMenuOpen(true)}
      onClose={() => setIsGitOperationsMenuOpen(false)}
      button={
        <ActionButton
          icon={AccountTreeIcon}
          tooltip="Git operations"
          disabled={!kgPermissions.includes(KgPermissions.Write)}
        />
      }
    >
      <List>
        {user?.isAdmin && (
          <ListItem disablePadding>
            <ListItemButton
              dense
              onClick={_ =>
                gitOperationRequest(
                  kgId => services.pullKnowledgeGraphFromMaster(kgId),
                  'Knowledge Graph successfully pulled from master!',
                  'There was an error pulling the knowledge graph from master.',
                  'Pull from master',
                  'Do you want to pull changes from master?',
                  'Pull'
                )
              }
            >
              <ListItemIcon>
                <ArrowDownwardIcon />
              </ListItemIcon>
              <ListItemText primary="Pull from master" />
            </ListItemButton>
          </ListItem>
        )}
        {user?.isAdmin && (
          <ListItem disablePadding>
            <ListItemButton
              dense
              onClick={_ =>
                gitOperationRequest(
                  kgId => services.pushKnowledgeGraphToMaster(kgId),
                  'Knowledge Graph successfully pushed to master!',
                  'There was an error pushing the knowledge graph to master.',
                  'Push to master',
                  'Do you want to push changes to master?',
                  'Push'
                )
              }
            >
              <ListItemIcon>
                <ArrowUpwardIcon />
              </ListItemIcon>
              <ListItemText primary="Push to master" />
            </ListItemButton>
          </ListItem>
        )}
        {user?.isAdmin && (
          <ListItem disablePadding>
            <ListItemButton
              dense
              onClick={_ =>
                gitOperationRequest(
                  kgId => services.resetKnowledgeGraphToMaster(kgId),
                  'Knowledge Graph successfully reset to master!',
                  'There was an error resetting the knowledge graph to master.',
                  'Reset to master',
                  'Do you really want to reset to master? You will lose all local changes!',
                  'Reset'
                )
              }
            >
              <ListItemIcon>
                <ClearIcon />
              </ListItemIcon>
              <ListItemText primary="Reset to master" />
            </ListItemButton>
          </ListItem>
        )}
        <ListItem disablePadding>
          <ListItemButton
            dense
            onClick={() => {
              setIsGitOperationsMenuOpen(false);
              createDevKnowledgeGraph();
            }}
          >
            <ListItemIcon>
              <SplitIcon />
            </ListItemIcon>
            <ListItemText primary="Create development branch" />
          </ListItemButton>
        </ListItem>
        {commonKgGitAndFilesActions}
      </List>
    </PopoverButton>
  );

  const devKgOperationsPopoverButton = (
    <PopoverButton
      button={
        <ActionButton
          icon={AccountTreeIcon}
          tooltip="Development knowledge graph sync operations"
          data-test="devKgOperations"
        />
      }
      isOpen={isGitOperationsMenuOpen}
      onOpened={() => setIsGitOperationsMenuOpen(true)}
      onClose={() => setIsGitOperationsMenuOpen(false)}
    >
      <List>
        <ListItem disablePadding>
          <ListItemButton
            dense
            onClick={_ =>
              gitOperationRequest(
                kgId => services.pullDevKgFromMainKg(kgId),
                'Knowledge Graph successfully pulled from main knowledge graph!',
                'There was an error pulling the knowledge graph from main knowledge graph.',
                'Pull from main knowledge graph',
                'Do you want to pull changes from the main knowledge graph?',
                'Pull'
              )
            }
            data-test="pullDevKgFromMainKg"
          >
            <ListItemIcon>
              <ArrowDownwardIcon />
            </ListItemIcon>
            <ListItemText primary="Pull changes from main knowledge graph" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            dense
            onClick={async () => {
              setIsGitOperationsMenuOpen(false);
              checkDevKgIsUpToDateBeforeTesting(() => {
                operationAffectingMultipleFiles(
                  'Push to main knowledge graph',
                  'This operation possibly',
                  'performing the operation',
                  null,
                  async reloadFrontend => {
                    testKg(allTestsPassed => {
                      const pushChanges = async () => {
                        const response = await withLoader(services.pushDevKgToMainKg(kgId));
                        setTestResultState(prev => ({ ...prev, isOpen: false }));
                        if (response.success) {
                          successMessage('The changes were successfully pushed to the main knowledge graph!');
                          await reloadFrontend(null);
                        } else {
                          const message =
                            response.response?.data ||
                            'There was an error pushing the changes to the main knowledge graph.';
                          errorMessage(message);
                        }
                      };
                      if (allTestsPassed) {
                        pushChanges();
                      } else {
                        showConfirmationModal({
                          title: 'Tests failed',
                          content:
                            'There are failing tests. Do you want to push the changes to the main knowledge graph anyway? ' +
                            'This will make the changes visible to all users. You may delete the development branch afterwards.',
                          confirmButtonText: 'Push anyway',
                          onConfirm: pushChanges
                        });
                      }
                    }, 'Push');
                  }
                );
              });
            }}
            data-test="pushDevKgToMainKg"
          >
            <ListItemIcon>
              <ArrowUpwardIcon />
            </ListItemIcon>
            <ListItemText primary="Push changes to main knowledge graph" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            dense
            onClick={_ =>
              gitOperationRequest(
                kgId => services.resetDevKgToMainKg(kgId),
                'Knowledge Graph successfully reset to main knowledge graph!',
                'There was an error resetting the knowledge graph to main knowledge graph.',
                'Reset to main knowledge graph',
                'Do you really want to reset to the main knowledge graph? You will lose all local changes!',
                'Reset'
              )
            }
            data-test="resetDevKgToMainKg"
          >
            <ListItemIcon>
              <ClearIcon />
            </ListItemIcon>
            <ListItemText primary="Reset to main knowledge graph" />
          </ListItemButton>
        </ListItem>
        {commonKgGitAndFilesActions}
      </List>
    </PopoverButton>
  );

  const testResultModal = testResultState.isOpen && (
    <TestResultModal
      testResult={testResult}
      isOpen={testResultState.isOpen}
      confirmButtonText={testResultState.confirmButtonText}
      onConfirm={testResultState.onConfirm}
      onClose={() => setTestResultState(prev => ({ ...prev, isOpen: false }))}
    />
  );

  const testKg = async (onConfirm, confirmButtonText) => {
    const response = await withLoader(services.testAnswers(kgId));
    if (response.success) {
      const testReport = response.data.data;
      setTestResultState({ isOpen: true, onConfirm, confirmButtonText });
      setTestResult(testReport);
    } else {
      errorMessage('There was an error testing the answers');
    }
  };

  return (
    <div className={classes.editorActionBar} data-test="editorActionBar">
      <div className={classes.treeTypeToggle}>
        <ToggleButtonGroup
          value={treeType}
          exclusive
          onChange={(event, newTreeType) => setTreeType(newTreeType || treeType)}
          size="small"
          data-test="treeTypeToggle"
        >
          <TooltipToggleButton
            value={treeTypes.concept}
            TooltipProps={{ title: 'Show the concept tree', placement: 'top' }}
            data-value={treeTypes.concept}
          >
            <SvgIcon component={HierarchyIcon} viewBox={HierarchyIcon().props.viewBox} />
          </TooltipToggleButton>
          <TooltipToggleButton
            value={treeTypes.file}
            TooltipProps={{ title: 'Show the file tree', placement: 'top' }}
            data-value={treeTypes.file}
          >
            <FolderIcon />
          </TooltipToggleButton>
        </ToggleButtonGroup>
      </div>
      <PopoverButton
        button={
          <ActionButton
            icon={NoteAddIcon}
            tooltip="Create file"
            data-test="editorCreateFileButton"
            disabled={!kgPermissions.includes(KgPermissions.Write)}
          />
        }
      >
        <List>
          {[...creatableFileTypes.entries()].map(([type, label]) => (
            <ListItem key={type} disablePadding>
              <ListItemButton
                dense
                onClick={_ => {
                  setNewFileType(type);
                  setNewFileIdentifier(null);
                }}
                data-test="editorFileTypeItem"
                data-value={type}
              >
                <ListItemText primary={label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </PopoverButton>

      <Dialog
        fullWidth
        maxWidth="xs"
        open={newFileType != null || fileIsCreating}
        onClose={_ => setNewFileType(null)}
        className={classes.createFileDialog}
      >
        <CreateFileForm
          createFile={createFile}
          fileIsCreating={fileIsCreating}
          newFileIdentifier={newFileIdentifier}
          newFileType={newFileType}
          setNewFileType={setNewFileType}
          setFileIsCreating={setFileIsCreating}
          setNewFileIdentifier={setNewFileIdentifier}
        />
      </Dialog>

      {knowledgeGraph?.data?.developmentKgForId ? devKgOperationsPopoverButton : gitOperationsPopoverButton}

      <ActionButton
        icon={BugReportIcon}
        tooltip="Test saved answers and rules"
        data-test="editorTestAnswers"
        disabled={!kgPermissions.includes(KgPermissions.Write)}
        onClick={() => withLoader(checkDevKgIsUpToDateBeforeTesting(testKg))}
      />
      {testResultModal}
      {CreateDevKgModal()}
    </div>
  );
};

export default withStyles(styles)(EditorLeftPanelActionBar);
